import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Paperclip, Send, Download, X, MessageCircle, FileText } from "lucide-react";

/**
 * Mission-scoped chat (spec §7).
 * Props:
 *   missionId — required
 *   myRole — "client" | "candidate" | "admin"
 *   headerLabel — displayed at the top
 *   otherPartyName — name shown to disambiguate the counterpart
 *
 * Uses `/missions/{missionId}/messages` and polls every 15 s.
 * Attachments: PDF/JPG/PNG/WEBP, 3 max × 5 MB.
 */
export default function MissionChat({ missionId, myRole, headerLabel, otherPartyName }) {
    const [messages, setMessages] = useState([]);
    const [body, setBody] = useState("");
    const [files, setFiles] = useState([]);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");
    const bottomRef = useRef(null);
    const fileInputRef = useRef(null);

    const scrollToEnd = () => {
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    };

    const refresh = async (initial = false) => {
        if (!missionId) return;
        try {
            const r = await api.get(`/missions/${missionId}/messages`);
            setMessages(r.data || []);
            setError("");
            if (initial) scrollToEnd();
        } catch (e) {
            const msg = e?.response?.data?.detail;
            if (msg) setError(msg);
        }
        try {
            await api.post(`/missions/${missionId}/messages/read`);
        } catch (_) { /* silent */ }
    };

    useEffect(() => {
        refresh(true);
        const iv = setInterval(() => refresh(false), 15000);
        return () => clearInterval(iv);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [missionId]);

    const onPickFile = (e) => {
        setFiles(Array.from(e.target.files || []).slice(0, 3));
        e.target.value = "";
    };
    const removeFile = (idx) => setFiles((fs) => fs.filter((_, i) => i !== idx));

    const send = async () => {
        if (!body.trim() && files.length === 0) return;
        setSending(true);
        try {
            const fd = new FormData();
            fd.append("body", body);
            files.forEach((f) => fd.append("files", f));
            await api.post(`/missions/${missionId}/messages`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setBody("");
            setFiles([]);
            await refresh(true);
            scrollToEnd();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur d'envoi");
        } finally {
            setSending(false);
        }
    };

    const downloadAttachment = async (msgId, att) => {
        try {
            const r = await api.get(`/missions/messages/${msgId}/attachments/${att.id}`, {
                responseType: "blob",
            });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = att.filename;
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 5000);
        } catch (_) {
            toast.error("Erreur de téléchargement");
        }
    };

    if (error) {
        return (
            <div
                className="sd-card p-6 text-center text-sm text-slate-500 bg-slate-50"
                data-testid={`mission-chat-locked-${missionId}`}
            >
                <MessageCircle size={20} className="text-slate-300 mx-auto mb-2" />
                {error}
            </div>
        );
    }

    return (
        <div
            className="sd-card flex flex-col overflow-hidden"
            style={{ height: 480 }}
            data-testid={`mission-chat-${missionId}`}
        >
            <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 bg-[#0B192C] text-white">
                <MessageCircle size={16} className="text-[#F5A623]" />
                <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{headerLabel || "Messagerie mission"}</div>
                    {otherPartyName && (
                        <div className="text-[11px] text-white/60">Conversation avec {otherPartyName}</div>
                    )}
                </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4 bg-slate-50/50 space-y-2" data-testid="mission-chat-msgs">
                {messages.length === 0 && (
                    <div className="text-center text-slate-400 text-xs py-10">
                        Envoyez le premier message — consignes, plan d'accès, tenue, changement d'horaires…
                    </div>
                )}
                {messages.map((m) => {
                    const mine = m.from_role === myRole && m.from_user_id !== undefined;
                    return (
                        <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                            <div
                                className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm shadow-sm ${
                                    mine
                                        ? "bg-[#0B192C] text-white rounded-br-sm"
                                        : "bg-white border border-slate-200 rounded-bl-sm"
                                }`}
                                data-testid={`mission-chat-msg-${m.id}`}
                            >
                                {m.body && <div className="whitespace-pre-wrap">{m.body}</div>}
                                {(m.attachments || []).map((att) => (
                                    <button
                                        key={att.id}
                                        onClick={() => downloadAttachment(m.id, att)}
                                        className={`mt-2 flex items-center gap-2 text-xs rounded-lg px-2 py-1 w-full text-left ${
                                            mine ? "bg-white/10 hover:bg-white/20" : "bg-slate-100 hover:bg-slate-200"
                                        }`}
                                    >
                                        <FileText size={12} />
                                        <span className="truncate flex-1">{att.filename}</span>
                                        <Download size={12} />
                                    </button>
                                ))}
                                <div
                                    className={`mt-1 text-[10px] ${
                                        mine ? "text-white/50" : "text-slate-400"
                                    }`}
                                >
                                    {new Date(m.created_at).toLocaleString("fr-FR", {
                                        day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
                                    })}
                                </div>
                            </div>
                        </div>
                    );
                })}
                <div ref={bottomRef} />
            </div>
            {files.length > 0 && (
                <div className="px-4 py-2 border-t border-slate-100 flex flex-wrap gap-2 bg-slate-50">
                    {files.map((f, i) => (
                        <div
                            key={i}
                            className="text-xs bg-white border border-slate-200 rounded-lg px-2 py-1 flex items-center gap-2"
                        >
                            <FileText size={12} className="text-[#F5A623]" />
                            <span className="max-w-[160px] truncate">{f.name}</span>
                            <span className="text-slate-400">{(f.size / 1024).toFixed(0)} Ko</span>
                            <button
                                onClick={() => removeFile(i)}
                                className="text-slate-400 hover:text-red-500"
                            >
                                <X size={12} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
            <div className="border-t border-slate-100 p-3 flex gap-2 items-end bg-white">
                <button
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2 rounded-lg text-slate-500 hover:text-[#0B192C] hover:bg-slate-100"
                    title="Ajouter une pièce jointe"
                    data-testid={`mission-chat-attach-${missionId}`}
                >
                    <Paperclip size={16} />
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf,image/jpeg,image/png,image/webp"
                    multiple
                    onChange={onPickFile}
                    className="hidden"
                />
                <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            send();
                        }
                    }}
                    rows={1}
                    maxLength={5000}
                    placeholder="Écrire un message… (Entrée pour envoyer, Maj+Entrée pour une nouvelle ligne)"
                    className="flex-1 resize-none border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623]/40 outline-none"
                    data-testid={`mission-chat-input-${missionId}`}
                />
                <button
                    onClick={send}
                    disabled={sending || (!body.trim() && files.length === 0)}
                    className="btn-gold px-4 py-2 text-sm disabled:opacity-50 inline-flex items-center gap-1"
                    data-testid={`mission-chat-send-${missionId}`}
                >
                    <Send size={14} /> {sending ? "…" : "Envoyer"}
                </button>
            </div>
        </div>
    );
}
